import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { ArrowLeft, Plus, Pencil, Trash2, Search, Lock, BookOpen, Mail, ChevronDown, X, Check, XCircle, RefreshCw, BarChart3, Sparkles, Tag, Download, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const TYPES = [
  { value: "poetry", label: "Poetry" },
  { value: "scripture", label: "Scripture" },
  { value: "quote", label: "Quote" },
  { value: "document", label: "Document" },
  { value: "song", label: "Song" },
  { value: "other", label: "Other" },
];

function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);

  const submit = async () => {
    try {
      await axios.post(`${API}/admin/verify`, { password: pw });
      onUnlock(pw);
    } catch {
      setError(true);
      setPw("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F5F0E8" }}>
      <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full mx-4" data-testid="admin-password-gate">
        <div className="text-center mb-6">
          <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: "#7A5946" }} />
          <h1 className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Admin Panel</h1>
          <p className="text-sm mt-1" style={{ color: "#7A5946" }}>Enter the admin password to continue</p>
        </div>
        <Input
          type="password"
          placeholder="Password"
          value={pw}
          onChange={e => { setPw(e.target.value); setError(false); }}
          onKeyDown={e => e.key === "Enter" && submit()}
          className="rounded-xl mb-3 text-center text-lg tracking-widest"
          style={{ borderColor: error ? "#EF4444" : "#D8C3B5" }}
          data-testid="admin-password-input"
        />
        {error && <p className="text-xs text-red-500 text-center mb-3">Incorrect password</p>}
        <button onClick={submit} className="tt-btn tt-btn-gold w-full py-3" data-testid="admin-password-submit">
          Enter
        </button>
      </div>
    </div>
  );
}

function TagPicker({ allTags, selectedTags, onChange }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");

  const toggle = (tag) => {
    onChange(selectedTags.includes(tag) ? selectedTags.filter(t => t !== tag) : [...selectedTags, tag]);
  };

  const addCustom = () => {
    const trimmed = custom.trim();
    if (trimmed && !selectedTags.includes(trimmed)) {
      onChange([...selectedTags, trimmed]);
    }
    setCustom("");
  };

  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full rounded-xl p-2.5 text-sm text-left flex items-center justify-between"
        style={{ border: "1px solid #D8C3B5", background: "white", color: selectedTags.length ? "#4A3224" : "#9CA3AF" }}
        data-testid="tag-picker-btn">
        <span>{selectedTags.length ? selectedTags.join(", ") : "Select tags..."}</span>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "#7A5946" }} />
      </button>
      {open && (
        <div className="mt-1 rounded-xl border p-3 max-h-60 overflow-y-auto" style={{ borderColor: "#D8C3B5", background: "white" }}>
          <div className="flex gap-2 mb-2">
            <Input placeholder="Add new tag..." value={custom} onChange={e => setCustom(e.target.value)}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustom())}
              className="rounded-lg text-sm flex-1" style={{ borderColor: "#D8C3B5" }}
              data-testid="tag-custom-input" />
            <Button size="sm" onClick={addCustom} className="rounded-lg" variant="outline" data-testid="tag-add-custom">Add</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allTags.map(t => (
              <button key={t.tag} onClick={() => toggle(t.tag)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedTags.includes(t.tag) ? "text-white" : ""}`}
                style={{
                  background: selectedTags.includes(t.tag) ? "#D97706" : "#F5F0E8",
                  color: selectedTags.includes(t.tag) ? "white" : "#4A3224",
                  border: `1px solid ${selectedTags.includes(t.tag) ? "#D97706" : "#D8C3B5"}`
                }}
                data-testid={`tag-option-${t.tag}`}>
                {t.tag} ({t.count})
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemForm({ item, allTags, onSave, onCancel, saving, submitLabel }) {
  const [form, setForm] = useState({
    title: item?.title || "",
    author: item?.author || "",
    content: item?.content || "",
    type: item?.type || "poetry",
    tags: item?.tags || [],
  });

  const handleSave = () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.content.trim()) { toast.error("Content is required"); return; }
    onSave(form);
  };

  return (
    <div className="space-y-3" data-testid="item-form">
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Type</label>
        <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
          className="w-full rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5" }}
          data-testid="form-type-select">
          {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Title *</label>
        <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
          placeholder="Enter title" className="rounded-xl" style={{ borderColor: "#D8C3B5" }}
          data-testid="form-title" />
      </div>
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Author</label>
        <Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })}
          placeholder="Enter author" className="rounded-xl" style={{ borderColor: "#D8C3B5" }}
          data-testid="form-author" />
      </div>
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Tags</label>
        <TagPicker allTags={allTags} selectedTags={form.tags} onChange={tags => setForm({ ...form, tags })} />
      </div>
      <div>
        <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Content *</label>
        <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
          placeholder="Paste or type the full text here"
          className="rounded-xl min-h-[200px] text-sm leading-6" style={{ borderColor: "#D8C3B5" }}
          data-testid="form-content" />
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={handleSave} disabled={saving} className="tt-btn tt-btn-gold flex-1 py-2.5" data-testid="form-save-btn">
          {saving ? "Saving..." : (submitLabel || (item ? "Update Item" : "Add Item"))}
        </button>
        <Button variant="ghost" onClick={onCancel} className="rounded-xl">Cancel</Button>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [password, setPassword] = useState(null);
  const [tab, setTab] = useState("library");
  const [items, setItems] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [approveRequest, setApproveRequest] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [stats, setStats] = useState(null);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [tagAction, setTagAction] = useState("rename"); // "rename" or "merge"
  const [tagOld, setTagOld] = useState("");
  const [tagNew, setTagNew] = useState("");
  const [tagSaving, setTagSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const params = { password };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (filterTag) params.tag = filterTag;
      const { data } = await axios.get(`${API}/admin/library`, { params });
      setItems(data);
    } catch { toast.error("Failed to load items"); }
    setLoading(false);
  }, [password, search, filterType, filterTag]);

  const fetchTags = useCallback(async () => {
    if (!password) return;
    try {
      const { data } = await axios.get(`${API}/admin/library/all-tags`, { params: { password } });
      setAllTags(data);
    } catch { /* silent */ }
  }, [password]);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/library/requests`);
      setRequests(data);
    } catch { /* silent */ }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!password) return;
    try {
      const { data } = await axios.get(`${API}/admin/library/stats`, { params: { password } });
      setStats(data);
    } catch { /* silent */ }
  }, [password]);

  useEffect(() => {
    if (password) { fetchItems(); fetchTags(); fetchRequests(); fetchStats(); }
  }, [password, fetchItems, fetchTags, fetchRequests, fetchStats]);

  // Debounced search
  useEffect(() => {
    if (!password) return;
    const timer = setTimeout(fetchItems, 300);
    return () => clearTimeout(timer);
  }, [search, filterType, filterTag, password, fetchItems]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/library/${editItem.id}?password=${password}`, form);
        toast.success("Item updated!");
      } else {
        await axios.post(`${API}/admin/library?password=${password}`, form);
        toast.success("Item added!");
      }
      setShowForm(false);
      setEditItem(null);
      fetchItems();
      fetchTags();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/library/${id}?password=${password}`);
      toast.success("Item deleted");
      setDeleteConfirm(null);
      fetchItems();
      fetchStats();
    } catch { toast.error("Failed to delete"); }
  };

  const handleApprove = async (form) => {
    if (!approveRequest) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/requests/${approveRequest.id}/approve?password=${password}`, form);
      toast.success(`"${form.title}" added to library!`);
      setApproveRequest(null);
      setSuggestedTags([]);
      fetchItems();
      fetchTags();
      fetchRequests();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
    }
    setSaving(false);
  };

  const handleDismiss = async (reqId) => {
    try {
      await axios.post(`${API}/admin/requests/${reqId}/dismiss?password=${password}`);
      toast.success("Request dismissed");
      fetchRequests();
    } catch { toast.error("Failed to dismiss"); }
  };

  const handleDeleteRequest = async (reqId) => {
    try {
      await axios.delete(`${API}/admin/requests/${reqId}?password=${password}`);
      toast.success("Request deleted");
      fetchRequests();
    } catch { toast.error("Failed to delete request"); }
  };

  const startApprove = async (req) => {
    setSuggestedTags([]);
    try {
      const { data } = await axios.post(`${API}/admin/requests/${req.id}/suggest-tags?password=${password}`);
      setSuggestedTags(data || []);
    } catch { /* silent */ }
    setApproveRequest(req);
  };

  const handleRegenerateDoc = async () => {
    setRegenerating(true);
    try {
      await axios.post(`${API}/admin/regenerate-doc?password=${password}`);
      toast.success("Word doc regenerated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate");
    }
    setRegenerating(false);
  };

  const handleTagAction = async () => {
    if (!tagOld.trim() || !tagNew.trim()) { toast.error("Both fields required"); return; }
    if (tagOld === tagNew) { toast.error("Tags must be different"); return; }
    setTagSaving(true);
    try {
      if (tagAction === "rename") {
        await axios.post(`${API}/admin/tags/rename?password=${password}`, { old_tag: tagOld, new_tag: tagNew });
        toast.success(`Renamed "${tagOld}" → "${tagNew}"`);
      } else {
        await axios.post(`${API}/admin/tags/merge?password=${password}`, { source_tag: tagOld, target_tag: tagNew });
        toast.success(`Merged "${tagOld}" into "${tagNew}"`);
      }
      setTagOld("");
      setTagNew("");
      setShowTagManager(false);
      fetchTags();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setTagSaving(false);
  };

  const handleExportCSV = () => {
    window.open(`${API}/admin/library/export-csv?password=${password}`, "_blank");
  };

  const handleExportDoc = () => {
    window.open(`${API}/library/export-doc`, "_blank");
  };

  if (!password) return <PasswordGate onUnlock={setPassword} />;

  return (
    <div className="min-h-screen" style={{ background: "#F5F0E8" }}>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm" style={{ color: "#7A5946" }}
            data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" /> Back to App
          </button>
          <h1 className="font-heading text-lg font-bold" style={{ color: "#4A3224" }}>Admin Panel</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 rounded-xl p-1" style={{ background: "#E8DDD0" }}>
          <button onClick={() => setTab("library")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-heading font-semibold flex items-center justify-center gap-2 transition-colors`}
            style={{ background: tab === "library" ? "white" : "transparent", color: tab === "library" ? "#4A3224" : "#7A5946" }}
            data-testid="admin-tab-library">
            <BookOpen className="w-4 h-4" /> Library
          </button>
          <button onClick={() => setTab("requests")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-heading font-semibold flex items-center justify-center gap-2 transition-colors`}
            style={{ background: tab === "requests" ? "white" : "transparent", color: tab === "requests" ? "#4A3224" : "#7A5946" }}
            data-testid="admin-tab-requests">
            <Mail className="w-4 h-4" /> Requests
            {requests.filter(r => !r.status || r.status === "pending").length > 0 && (
              <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                style={{ background: "#FB7185" }}>{requests.filter(r => !r.status || r.status === "pending").length}</span>
            )}
          </button>
          <button onClick={() => setTab("stats")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-heading font-semibold flex items-center justify-center gap-2 transition-colors`}
            style={{ background: tab === "stats" ? "white" : "transparent", color: tab === "stats" ? "#4A3224" : "#7A5946" }}
            data-testid="admin-tab-stats">
            <BarChart3 className="w-4 h-4" /> Stats
          </button>
        </div>

        {/* Library Manager Tab */}
        {tab === "library" && (
          <div data-testid="admin-library-section">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#7A5946" }} />
                <Input className="pl-9 rounded-xl" style={{ borderColor: "#D8C3B5", background: "white" }}
                  placeholder="Search by title, author, or content..." value={search}
                  onChange={e => setSearch(e.target.value)} data-testid="admin-search" />
              </div>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                className="rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5", background: "white", minWidth: 120 }}
                data-testid="admin-type-filter">
                <option value="">All Types</option>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
                className="rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5", background: "white", minWidth: 120 }}
                data-testid="admin-tag-filter">
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
              </select>
              <button onClick={() => { setEditItem(null); setShowForm(true); }}
                className="tt-btn tt-btn-gold px-4 py-2.5 text-sm flex items-center gap-2 whitespace-nowrap"
                data-testid="admin-add-btn">
                <Plus className="w-4 h-4" /> Add Item
              </button>
            </div>

            {/* Count + Actions */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs" style={{ color: "#7A5946" }}>
                {loading ? "Loading..." : `${items.length} item${items.length !== 1 ? "s" : ""} found`}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button onClick={handleExportDoc}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ background: "#F5F0E8", color: "#7A5946", border: "1px solid #D8C3B5" }}
                  data-testid="admin-export-doc">
                  <FileText className="w-3.5 h-3.5" /> Word Doc
                </button>
                <button onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ background: "#F5F0E8", color: "#7A5946", border: "1px solid #D8C3B5" }}
                  data-testid="admin-export-csv">
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
                <button onClick={handleRegenerateDoc} disabled={regenerating}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                  style={{ background: "#F5F0E8", color: "#7A5946", border: "1px solid #D8C3B5" }}
                  data-testid="admin-regenerate-doc">
                  <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} />
                  {regenerating ? "..." : "Refresh"}
                </button>
                <button onClick={() => setShowTagManager(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}
                  data-testid="admin-tag-manager-btn">
                  <Tag className="w-3.5 h-3.5" /> Tags
                </button>
              </div>
            </div>

            {/* Items List */}
            <div className="space-y-2">
              {items.map(item => (
                <div key={item.id} className="bg-white rounded-xl p-3 flex items-start gap-3"
                  style={{ border: "1px solid #E8DDD0" }} data-testid={`admin-item-${item.id}`}>
                  <div className="w-2 h-12 rounded-full flex-shrink-0 mt-1"
                    style={{ background: TYPES.find(t => t.value === item.type)?.value === item.type ?
                      ({ poetry: "#A855F7", scripture: "#F59E0B", quote: "#06B6D4", document: "#3B82F6", song: "#FB7185", other: "#4ADE80" }[item.type] || "#9CA3AF") : "#9CA3AF" }} />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
                    <p className="text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                    <p className="text-xs" style={{ color: "#7A5946" }}>
                      {item.author && `by ${item.author} · `}{item.type}
                    </p>
                    {item.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.tags.map(tag => (
                          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full"
                            style={{ background: "#F5F0E8", color: "#7A5946" }}>{tag}</span>
                        ))}
                      </div>
                    )}
                    {expandedItem === item.id && (
                      <pre className="mt-2 text-xs whitespace-pre-wrap leading-5 p-2 rounded-lg"
                        style={{ background: "#FAFAF8", color: "#4A3224", fontFamily: "'Nunito', sans-serif" }}>
                        {item.content}
                      </pre>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => { setEditItem(item); setShowForm(true); }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`admin-edit-${item.id}`}>
                      <Pencil className="w-4 h-4" style={{ color: "#7A5946" }} />
                    </button>
                    <button onClick={() => setDeleteConfirm(item)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      data-testid={`admin-delete-${item.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: "#EF4444" }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Poem Requests Tab */}
        {tab === "requests" && (
          <div data-testid="admin-requests-section">
            <p className="text-xs mb-4" style={{ color: "#7A5946" }}>
              {requests.length} request{requests.length !== 1 ? "s" : ""} from users
            </p>
            {requests.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center" style={{ border: "1px solid #E8DDD0" }}>
                <p className="text-sm" style={{ color: "#7A5946" }}>No requests yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map(req => {
                  const isPending = !req.status || req.status === "pending";
                  const statusColors = {
                    pending: { bg: "#FEF3C7", color: "#92400E" },
                    approved: { bg: "#DCFCE7", color: "#166534" },
                    dismissed: { bg: "#F3F4F6", color: "#6B7280" },
                  };
                  const sc = statusColors[req.status] || statusColors.pending;
                  return (
                    <div key={req.id} className="bg-white rounded-xl p-4" style={{ border: "1px solid #E8DDD0" }}
                      data-testid={`admin-request-${req.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold" style={{ color: "#4A3224" }}>{req.title}</p>
                          {req.author && <p className="text-xs mt-0.5" style={{ color: "#7A5946" }}>by {req.author}</p>}
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: sc.bg, color: sc.color }}>
                          {req.status || "pending"}
                        </span>
                      </div>
                      {req.content && (
                        <pre className="text-xs mt-2 p-2 rounded-lg whitespace-pre-wrap leading-5"
                          style={{ background: "#FAFAF8", color: "#4A3224", fontFamily: "'Nunito', sans-serif" }}>
                          {req.content}
                        </pre>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-[10px]" style={{ color: "#B5A08C" }}>
                          {req.created_at ? new Date(req.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                        </p>
                        <div className="flex gap-2">
                          {isPending && (
                            <>
                              <button onClick={() => startApprove(req)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors"
                                style={{ background: "#16A34A" }}
                                data-testid={`approve-request-${req.id}`}>
                                <Check className="w-3.5 h-3.5" /> Approve & Add
                              </button>
                              <button onClick={() => handleDismiss(req.id)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                                style={{ background: "#F3F4F6", color: "#6B7280" }}
                                data-testid={`dismiss-request-${req.id}`}>
                                <XCircle className="w-3.5 h-3.5" /> Dismiss
                              </button>
                            </>
                          )}
                          <button onClick={() => handleDeleteRequest(req.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            data-testid={`delete-request-${req.id}`}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: "#EF4444" }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {tab === "stats" && stats && (
          <div data-testid="admin-stats-section">
            {/* Total */}
            <div className="bg-white rounded-xl p-5 mb-4 text-center" style={{ border: "1px solid #E8DDD0" }}>
              <p className="font-heading text-3xl font-bold" style={{ color: "#D97706" }}>{stats.total}</p>
              <p className="text-sm" style={{ color: "#7A5946" }}>Total Library Items</p>
            </div>

            {/* By Type */}
            <div className="bg-white rounded-xl p-5 mb-4" style={{ border: "1px solid #E8DDD0" }} data-testid="stats-by-type">
              <h3 className="font-heading text-sm font-bold mb-3" style={{ color: "#4A3224" }}>Items by Type</h3>
              <div className="space-y-2">
                {stats.by_type.map(t => {
                  const pct = stats.total ? (t.count / stats.total) * 100 : 0;
                  const colors = { poetry: "#A855F7", scripture: "#F59E0B", quote: "#06B6D4", document: "#3B82F6", song: "#FB7185", other: "#4ADE80" };
                  return (
                    <div key={t.type} className="flex items-center gap-3">
                      <span className="text-xs font-semibold w-20 capitalize" style={{ color: "#4A3224" }}>{t.type}</span>
                      <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: "#F5F0E8" }}>
                        <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all"
                          style={{ width: `${Math.max(pct, 8)}%`, background: colors[t.type] || "#9CA3AF" }}>
                          <span className="text-[10px] font-bold text-white">{t.count}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Tags */}
            <div className="bg-white rounded-xl p-5" style={{ border: "1px solid #E8DDD0" }} data-testid="stats-top-tags">
              <h3 className="font-heading text-sm font-bold mb-3" style={{ color: "#4A3224" }}>Top 15 Tags</h3>
              <div className="flex flex-wrap gap-2">
                {stats.top_tags.map(t => (
                  <span key={t.tag} className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>
                    {t.tag} <span className="font-bold">({t.count})</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditItem(null); } }}>
          <DialogContent className="sm:max-w-[600px] p-6 rounded-2xl overflow-y-auto" style={{ maxHeight: "90vh" }}
            data-testid="admin-form-dialog">
            <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>
              {editItem ? "Edit Item" : "Add New Item"}
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#7A5946" }}>
              {editItem ? "Update the library item below." : "Fill in the details to add a new item to the library."}
            </DialogDescription>
            <ItemForm item={editItem} allTags={allTags} onSave={handleSave} onCancel={() => { setShowForm(false); setEditItem(null); }} saving={saving} />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
          <DialogContent className="sm:max-w-[400px] p-6 rounded-2xl" data-testid="admin-delete-dialog">
            <DialogTitle className="font-heading text-lg font-bold" style={{ color: "#4A3224" }}>Delete Item?</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#7A5946" }}>
              This will permanently remove &ldquo;{deleteConfirm?.title}&rdquo; from the library. Users who already added it to their collection will keep their copy.
            </DialogDescription>
            <div className="flex gap-3 mt-4">
              <button onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#EF4444" }}
                data-testid="admin-delete-confirm">
                Delete
              </button>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="flex-1 rounded-xl">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Approve Request Dialog */}
        <Dialog open={!!approveRequest} onOpenChange={(o) => { if (!o) setApproveRequest(null); }}>
          <DialogContent className="sm:max-w-[600px] p-6 rounded-2xl overflow-y-auto" style={{ maxHeight: "90vh" }}
            data-testid="admin-approve-dialog">
            <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>
              Approve & Add to Library
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#7A5946" }}>
              Review and edit the details before adding this to the public library. The request will be marked as approved.
            </DialogDescription>
            {approveRequest && (
              <>
                {suggestedTags.length > 0 && (
                  <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "#7A5946" }}>
                    <Sparkles className="w-3.5 h-3.5" style={{ color: "#D97706" }} />
                    <span>Tags auto-suggested based on content</span>
                  </div>
                )}
                <ItemForm
                  item={{ title: approveRequest.title, author: approveRequest.author, content: approveRequest.content, type: "poetry", tags: suggestedTags }}
                  allTags={allTags}
                  onSave={handleApprove}
                  onCancel={() => { setApproveRequest(null); setSuggestedTags([]); }}
                  saving={saving}
                  submitLabel="Approve & Add to Library"
                />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Tag Manager Dialog */}
        <Dialog open={showTagManager} onOpenChange={setShowTagManager}>
          <DialogContent className="sm:max-w-[450px] p-6 rounded-2xl" data-testid="admin-tag-manager-dialog">
            <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Tag Manager</DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#7A5946" }}>
              Rename a tag across all items, or merge one tag into another.
            </DialogDescription>
            <div className="flex gap-2 mt-3 mb-4 rounded-lg p-1" style={{ background: "#F5F0E8" }}>
              <button onClick={() => setTagAction("rename")}
                className="flex-1 py-2 rounded-md text-xs font-semibold transition-colors"
                style={{ background: tagAction === "rename" ? "white" : "transparent", color: "#4A3224" }}
                data-testid="tag-action-rename">
                Rename
              </button>
              <button onClick={() => setTagAction("merge")}
                className="flex-1 py-2 rounded-md text-xs font-semibold transition-colors"
                style={{ background: tagAction === "merge" ? "white" : "transparent", color: "#4A3224" }}
                data-testid="tag-action-merge">
                Merge
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>
                  {tagAction === "rename" ? "Current tag name" : "Tag to merge (will be removed)"}
                </label>
                <select value={tagOld} onChange={e => setTagOld(e.target.value)}
                  className="w-full rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5" }}
                  data-testid="tag-old-select">
                  <option value="">Select tag...</option>
                  {allTags.map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>
                  {tagAction === "rename" ? "New tag name" : "Merge into (will be kept)"}
                </label>
                {tagAction === "merge" ? (
                  <select value={tagNew} onChange={e => setTagNew(e.target.value)}
                    className="w-full rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5" }}
                    data-testid="tag-new-select">
                    <option value="">Select target tag...</option>
                    {allTags.filter(t => t.tag !== tagOld).map(t => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
                  </select>
                ) : (
                  <Input value={tagNew} onChange={e => setTagNew(e.target.value)}
                    placeholder="Enter new tag name" className="rounded-xl" style={{ borderColor: "#D8C3B5" }}
                    data-testid="tag-new-input" />
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleTagAction} disabled={tagSaving}
                  className="tt-btn tt-btn-gold flex-1 py-2.5 disabled:opacity-50" data-testid="tag-action-submit">
                  {tagSaving ? "Saving..." : tagAction === "rename" ? "Rename Tag" : "Merge Tags"}
                </button>
                <Button variant="ghost" onClick={() => setShowTagManager(false)} className="rounded-xl">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
